import type { Hero } from "@/types/hero.types"
import type {
  ItemDefinition,
  InventoryItem,
  HeroInventory,
  DroppedItem,
  ActiveItemEffect,
  ItemEffectType,
} from "@/types/item.types"
import type { Position } from "@/types/game.types"
import { itemDefinitions, getUnlockedItems } from "@/data/item-types"
import { generateId } from "@/utils/id-generator"
import { gameNow } from "@/utils/game-clock"

// Maximum inventory slots
export const MAX_INVENTORY_SLOTS = 6

// Create an empty inventory
export const createEmptyInventory = (): HeroInventory => ({
  slots: Array(MAX_INVENTORY_SLOTS).fill(null),
})

// Create a starting inventory with the Clone Activation Codex in slot 1 and empty slots
export const createStartingInventory = (): HeroInventory => {
  const slots: (InventoryItem | null)[] = Array(MAX_INVENTORY_SLOTS).fill(null)

  // Slot 0 always has the Clone Activation Codex with 0 charges
  slots[0] = {
    id: generateId("item"),
    itemDefId: "clone-activation-beacon",
    quantity: 1,
    currentCooldown: 0,
    charges: 0,
  }

  return { slots }
}

// Check if an item is the Clone Activation Codex (cannot be dropped/removed)
export const isCloneBeacon = (itemDefId: string): boolean => {
  return itemDefId === "clone-activation-beacon"
}

// Increment beacon charges when a clone is purchased
export const incrementBeaconCharges = (inventory: HeroInventory): HeroInventory => {
  const newSlots = [...inventory.slots]

  // Beacon is always in slot 0
  const beaconSlot = newSlots[0]
  if (beaconSlot && beaconSlot.itemDefId === "clone-activation-beacon") {
    newSlots[0] = {
      ...beaconSlot,
      charges: (beaconSlot.charges || 0) + 1,
    }
  }

  return { slots: newSlots }
}

// Decrement beacon charges when a clone is used for restoration
export const decrementBeaconCharges = (inventory: HeroInventory): HeroInventory => {
  const newSlots = [...inventory.slots]

  // Beacon is always in slot 0
  const beaconSlot = newSlots[0]
  if (beaconSlot && beaconSlot.itemDefId === "clone-activation-beacon" && beaconSlot.charges && beaconSlot.charges > 0) {
    newSlots[0] = {
      ...beaconSlot,
      charges: beaconSlot.charges - 1,
    }
  }

  return { slots: newSlots }
}

// Get current beacon charges
export const getBeaconCharges = (inventory: HeroInventory): number => {
  const beaconSlot = inventory.slots[0]
  if (beaconSlot && beaconSlot.itemDefId === "clone-activation-beacon") {
    return beaconSlot.charges || 0
  }
  return 0
}

// Calculate clone charge cost based on hero level
// Uses droppod menu pricing formula: 50 * 1.5^(level-1)
// Level 1: 50, Level 2: 75, Level 3: 112, Level 4: 168...
export const getCloneChargeCostByLevel = (heroLevel: number): number => {
  return Math.floor(50 * Math.pow(1.5, heroLevel - 1))
}

// Get item definition by ID
export const getItemDefinition = (itemDefId: string): ItemDefinition | null => {
  return itemDefinitions[itemDefId] || null
}

// Find an empty slot in inventory
export const findEmptySlot = (inventory: HeroInventory): number => {
  return inventory.slots.findIndex((slot) => slot === null)
}

// Find a slot with a stackable item that can accept more
export const findStackableSlot = (inventory: HeroInventory, itemDefId: string): number => {
  const itemDef = getItemDefinition(itemDefId)
  if (!itemDef || !itemDef.stackable) return -1

  return inventory.slots.findIndex((slot) => {
    if (!slot) return false
    return slot.itemDefId === itemDefId && slot.quantity < itemDef.maxStack
  })
}

// Check if inventory has an empty slot
export const hasInventorySpace = (inventory: HeroInventory): boolean => {
  return findEmptySlot(inventory) !== -1
}

// Check if an item can be stacked with existing inventory items
export const canStackItem = (inventory: HeroInventory, itemDefId: string): boolean => {
  return findStackableSlot(inventory, itemDefId) !== -1
}

// Add item to inventory (finds empty slot or stacks)
export const addItemToInventory = (
  inventory: HeroInventory,
  itemDefId: string,
  quantity: number = 1,
): { inventory: HeroInventory; added: boolean; overflow: number } => {
  const itemDef = getItemDefinition(itemDefId)
  if (!itemDef) {
    return { inventory, added: false, overflow: quantity }
  }

  let remaining = quantity
  const newSlots = [...inventory.slots]

  // First, try to stack with existing items
  if (itemDef.stackable) {
    for (let i = 0; i < newSlots.length && remaining > 0; i++) {
      const slot = newSlots[i]
      if (slot && slot.itemDefId === itemDefId && slot.quantity < itemDef.maxStack) {
        const canAdd = Math.min(remaining, itemDef.maxStack - slot.quantity)
        newSlots[i] = { ...slot, quantity: slot.quantity + canAdd }
        remaining -= canAdd
      }
    }
  }

  // Then, try to use empty slots
  while (remaining > 0) {
    const emptyIdx = newSlots.findIndex((s) => s === null)
    if (emptyIdx === -1) break

    const toAdd = itemDef.stackable ? Math.min(remaining, itemDef.maxStack) : 1
    newSlots[emptyIdx] = {
      id: generateId("item"),
      itemDefId,
      quantity: toAdd,
      currentCooldown: 0,
    }
    remaining -= toAdd
  }

  return {
    inventory: { slots: newSlots },
    added: remaining < quantity,
    overflow: remaining,
  }
}

// Remove item from inventory
export const removeItemFromInventory = (
  inventory: HeroInventory,
  slotIndex: number,
  quantity: number = 1,
): HeroInventory => {
  if (slotIndex < 0 || slotIndex >= inventory.slots.length) {
    return inventory
  }

  const slot = inventory.slots[slotIndex]
  if (!slot) return inventory

  const newSlots = [...inventory.slots]

  if (slot.quantity <= quantity) {
    // Remove entire stack
    newSlots[slotIndex] = null
  } else {
    // Reduce quantity
    newSlots[slotIndex] = { ...slot, quantity: slot.quantity - quantity }
  }

  return { slots: newSlots }
}

// Create a dropped item on the ground
export const createDroppedItem = (position: Position, itemDefId: string): DroppedItem => {
  return {
    id: generateId("dropped"),
    itemDefId,
    position: { ...position },
    createdAt: gameNow(),
  }
}

// Check if item can be used (not on cooldown, consumable type)
export const canUseItem = (inventory: HeroInventory, slotIndex: number): boolean => {
  const slot = inventory.slots[slotIndex]
  if (!slot) return false

  const itemDef = getItemDefinition(slot.itemDefId)
  if (!itemDef) return false

  // Equipment items are applied on pickup, not used
  if (itemDef.type === "equipment") return false

  // Check cooldown
  if (slot.currentCooldown > 0) return false

  return true
}

// Use an item from inventory
export const useItem = (
  hero: Hero,
  inventory: HeroInventory,
  slotIndex: number,
): {
  hero: Hero
  inventory: HeroInventory
  consumed: boolean
  activeEffects: ActiveItemEffect[]
} => {
  if (!canUseItem(inventory, slotIndex)) {
    return { hero, inventory, consumed: false, activeEffects: [] }
  }

  const slot = inventory.slots[slotIndex]!
  const itemDef = getItemDefinition(slot.itemDefId)!

  let updatedHero = { ...hero }
  const activeEffects: ActiveItemEffect[] = []

  // Apply item effects
  for (const effect of itemDef.effects) {
    switch (effect.type) {
      case "heal":
        if (effect.duration) {
          // Heal over time
          activeEffects.push({
            id: generateId("effect"),
            itemDefId: itemDef.id,
            effectType: "heal",
            value: effect.value,
            remainingDuration: effect.duration,
            tickInterval: 60, // 1 second between ticks
            nextTickIn: 60,
          })
        } else {
          // Instant heal - cap at effective max health (base + equipment bonuses)
          const effectiveMaxHealth = getEffectiveMaxHealth(updatedHero, inventory)
          updatedHero = {
            ...updatedHero,
            health: Math.min(effectiveMaxHealth, updatedHero.health + effect.value),
          }
        }
        break

      case "mana": {
        // Cap at effective max mana (base + equipment bonuses if any)
        const effectiveMaxMana = getEffectiveMaxMana(updatedHero, inventory)
        updatedHero = {
          ...updatedHero,
          mana: Math.min(effectiveMaxMana, updatedHero.mana + effect.value),
        }
        break
        }

      case "absorb":
        activeEffects.push({
          id: generateId("effect"),
          itemDefId: itemDef.id,
          effectType: "absorb",
          value: effect.value,
          remainingDuration: effect.duration || 1800,
        })
        break

      case "lure":
        activeEffects.push({
          id: generateId("effect"),
          itemDefId: itemDef.id,
          effectType: "lure",
          value: effect.value, // radius
          remainingDuration: effect.duration || 480,
        })
        break

      case "teleport":
        // Teleport is handled separately by the game system
        // (requires target selection)
        activeEffects.push({
          id: generateId("effect"),
          itemDefId: itemDef.id,
          effectType: "teleport",
          value: 0,
          remainingDuration: 1, // Instant, but needs processing
        })
        break

      // Other effects handled elsewhere
      default:
        break
    }
  }

  // Set cooldown on the item slot
  const newSlots = [...inventory.slots]
  newSlots[slotIndex] = {
    ...slot,
    quantity: slot.quantity - 1,
    currentCooldown: itemDef.cooldown || 0,
  }

  // Remove if quantity is 0
  if (newSlots[slotIndex]!.quantity <= 0) {
    newSlots[slotIndex] = null
  }

  return {
    hero: updatedHero,
    inventory: { slots: newSlots },
    consumed: true,
    activeEffects,
  }
}

// Apply equipment effects (permanent stat boosts)
export const applyEquipmentEffects = (hero: Hero, itemDef: ItemDefinition): Hero => {
  if (itemDef.type !== "equipment") return hero

  let updatedHero = { ...hero }

  for (const effect of itemDef.effects) {
    switch (effect.type) {
      case "armor":
        updatedHero = { ...updatedHero, armor: updatedHero.armor + effect.value }
        break

      case "max-health":
        updatedHero = {
          ...updatedHero,
          maxHealth: updatedHero.maxHealth + effect.value,
          health: updatedHero.health + effect.value, // Also increase current health
        }
        break

      case "damage-boost":
        updatedHero = {
          ...updatedHero,
          damage: {
            min: updatedHero.damage.min + effect.value,
            max: updatedHero.damage.max + effect.value,
          },
        }
        break

      // spell-power and lifesteal are checked during damage calculation
      default:
        break
    }
  }

  return updatedHero
}

// Get random item based on Siegesmith ultimate level
export const getRandomItemForLevel = (skillLevel: number): string | null => {
  const unlockedItems = getUnlockedItems(skillLevel)
  if (unlockedItems.length === 0) return null

  const randomItem = unlockedItems[Math.floor(Math.random() * unlockedItems.length)]
  return randomItem.id
}

// Update item cooldowns
export const updateItemCooldowns = (inventory: HeroInventory, delta: number): HeroInventory => {
  let changed = false
  const newSlots = inventory.slots.map((slot) => {
    if (!slot || slot.currentCooldown <= 0) return slot
    changed = true
    return { ...slot, currentCooldown: Math.max(0, slot.currentCooldown - delta) }
  })

  return changed ? { slots: newSlots } : inventory
}

// Update active item effects (HoTs, shields, etc.)
export const updateActiveItemEffects = (
  hero: Hero,
  effects: ActiveItemEffect[],
  delta: number,
  inventory: HeroInventory,
): { hero: Hero; effects: ActiveItemEffect[] } => {
  if (effects.length === 0) return { hero, effects }

  let updatedHero = { ...hero }
  const updatedEffects: ActiveItemEffect[] = []
  // Calculate effective max health once for HoT healing caps
  const effectiveMaxHealth = getEffectiveMaxHealth(hero, inventory)

  for (const effect of effects) {
    const remaining = effect.remainingDuration - delta
    if (remaining <= 0) continue // Effect expired

    const updatedEffect = { ...effect, remainingDuration: remaining }

    // Handle ticking effects (HoTs) - only if hero is alive
    if (effect.tickInterval && effect.nextTickIn !== undefined && updatedHero.health > 0) {
      let nextTick = effect.nextTickIn - delta
      while (nextTick <= 0 && remaining > 0) {
        // Apply tick
        if (effect.effectType === "heal") {
          const healPerTick = effect.value / (effect.remainingDuration / effect.tickInterval)
          updatedHero = {
            ...updatedHero,
            health: Math.min(effectiveMaxHealth, updatedHero.health + healPerTick),
          }
        }
        nextTick += effect.tickInterval
      }
      updatedEffect.nextTickIn = nextTick
    }

    updatedEffects.push(updatedEffect)
  }

  return { hero: updatedHero, effects: updatedEffects }
}

// Apply damage through absorb shields
export const applyDamageWithAbsorb = (
  incomingDamage: number,
  effects: ActiveItemEffect[],
): { actualDamage: number; effects: ActiveItemEffect[] } => {
  let remainingDamage = incomingDamage
  const updatedEffects: ActiveItemEffect[] = []

  for (const effect of effects) {
    if (effect.effectType === "absorb" && effect.value > 0) {
      const absorbed = Math.min(remainingDamage, effect.value)
      remainingDamage -= absorbed
      const newValue = effect.value - absorbed

      if (newValue > 0) {
        updatedEffects.push({ ...effect, value: newValue })
      }
      // If shield depleted, it's removed (not added back)
    } else {
      updatedEffects.push(effect)
    }
  }

  return { actualDamage: remainingDamage, effects: updatedEffects }
}

// Check if hero has lifesteal from equipment
export const getLifestealPercent = (inventory: HeroInventory): number => {
  let lifesteal = 0

  for (const slot of inventory.slots) {
    if (!slot) continue
    const itemDef = getItemDefinition(slot.itemDefId)
    if (!itemDef || itemDef.type !== "equipment") continue

    for (const effect of itemDef.effects) {
      if (effect.type === "lifesteal") {
        lifesteal += effect.value
      }
    }
  }

  return lifesteal
}

// Check if hero has spell power bonus from equipment
export const getSpellPowerBonus = (inventory: HeroInventory): number => {
  let spellPower = 0

  for (const slot of inventory.slots) {
    if (!slot) continue
    const itemDef = getItemDefinition(slot.itemDefId)
    if (!itemDef || itemDef.type !== "equipment") continue

    for (const effect of itemDef.effects) {
      if (effect.type === "spell-power") {
        spellPower += effect.value
      }
    }
  }

  return spellPower
}

// Get armor bonus from equipment in inventory
export const getEquipmentArmorBonus = (inventory: HeroInventory): number => {
  let armorBonus = 0

  for (const slot of inventory.slots) {
    if (!slot) continue
    const itemDef = getItemDefinition(slot.itemDefId)
    if (!itemDef || itemDef.type !== "equipment") continue

    for (const effect of itemDef.effects) {
      if (effect.type === "armor") {
        armorBonus += effect.value
      }
    }
  }

  return armorBonus
}

// Get max health bonus from equipment in inventory
export const getEquipmentMaxHealthBonus = (inventory: HeroInventory): number => {
  let healthBonus = 0

  for (const slot of inventory.slots) {
    if (!slot) continue
    const itemDef = getItemDefinition(slot.itemDefId)
    if (!itemDef || itemDef.type !== "equipment") continue

    for (const effect of itemDef.effects) {
      if (effect.type === "max-health") {
        healthBonus += effect.value
      }
    }
  }

  return healthBonus
}

// Get effective max health (base + equipment bonuses)
export const getEffectiveMaxHealth = (hero: Hero, inventory: HeroInventory): number => {
  return hero.maxHealth + getEquipmentMaxHealthBonus(inventory)
}

// Get effective max mana (base + equipment bonuses) - currently no max mana equipment exists
export const getEffectiveMaxMana = (hero: Hero, _inventory: HeroInventory): number => {
  // No max mana equipment bonuses currently, but function exists for future use
  return hero.maxMana
}

// Get damage boost from equipment in inventory
export const getEquipmentDamageBonus = (inventory: HeroInventory): number => {
  let damageBonus = 0

  for (const slot of inventory.slots) {
    if (!slot) continue
    const itemDef = getItemDefinition(slot.itemDefId)
    if (!itemDef || itemDef.type !== "equipment") continue

    for (const effect of itemDef.effects) {
      if (effect.type === "damage-boost") {
        damageBonus += effect.value
      }
    }
  }

  return damageBonus
}

// Get gold bonus multiplier from equipment in inventory (e.g., Ledger of Fortune)
export const getEquipmentGoldBonus = (inventory: HeroInventory): number => {
  let goldBonus = 0

  for (const slot of inventory.slots) {
    if (!slot) continue
    const itemDef = getItemDefinition(slot.itemDefId)
    if (!itemDef || itemDef.type !== "equipment") continue

    for (const effect of itemDef.effects) {
      if (effect.type === "gold-bonus") {
        goldBonus += effect.value
      }
    }
  }

  return goldBonus
}

/**
 * Sum one effect type across equipped items.
 *
 * Every equipment getter in this file walks the same loop; the older ones predate
 * this helper and are left as they are rather than churned.
 */
const sumEquipmentEffect = (inventory: HeroInventory, type: ItemEffectType): number => {
  let total = 0
  for (const slot of inventory.slots) {
    if (!slot) continue
    const itemDef = getItemDefinition(slot.itemDefId)
    if (!itemDef || itemDef.type !== "equipment") continue
    for (const effect of itemDef.effects) {
      if (effect.type === type) total += effect.value
    }
  }
  return total
}

/**
 * Fractional attack-speed bonus (0.3 = 30% faster swings).
 *
 * Floored just above -1 so a future penalty item can never divide the attack
 * cooldown by zero or flip its sign.
 */
export const getEquipmentAttackSpeedBonus = (inventory: HeroInventory): number =>
  Math.max(-0.9, sumEquipmentEffect(inventory, "attack-speed"))

/**
 * Added critical-hit chance from equipment, as a fraction.
 *
 * Stacks with the hero's passive crit chance; the total is clamped to 1 so a
 * heavily stacked build cannot exceed "always crits".
 */
export const getEquipmentCritChance = (inventory: HeroInventory): number =>
  Math.max(0, Math.min(1, sumEquipmentEffect(inventory, "crit-chance")))

/**
 * Fractional skill-cooldown reduction (0.3 = cooldowns last 70% as long).
 *
 * Capped at 0.9 so cooldowns always tick at a finite rate.
 */
export const getEquipmentCooldownReduction = (inventory: HeroInventory): number =>
  Math.max(0, Math.min(0.9, sumEquipmentEffect(inventory, "cooldown-reduction")))

/** Extra health regenerated per second from equipment. */
export const getEquipmentHpRegen = (inventory: HeroInventory): number =>
  sumEquipmentEffect(inventory, "hp-regen")

/** Extra mana regenerated per second from equipment. */
export const getEquipmentManaRegen = (inventory: HeroInventory): number =>
  sumEquipmentEffect(inventory, "mana-regen")

// Get speed bonus multiplier from equipment in inventory (e.g., Helm of Haste, Ledger of Greed)
export const getEquipmentSpeedBonus = (inventory: HeroInventory): number => {
  let speedBonus = 0

  for (const slot of inventory.slots) {
    if (!slot) continue
    const itemDef = getItemDefinition(slot.itemDefId)
    if (!itemDef || itemDef.type !== "equipment") continue

    for (const effect of itemDef.effects) {
      if (effect.type === "speed-boost") {
        speedBonus += effect.value
      }
    }
  }

  return speedBonus
}

// Get total damage reflect percentage from equipment (e.g., Dwarven Rapier, Titan's Gauntlet, Thornmail Hauberk)
export const getDamageReflectPercent = (inventory: HeroInventory): number => {
  let reflect = 0

  for (const slot of inventory.slots) {
    if (!slot) continue
    const itemDef = getItemDefinition(slot.itemDefId)
    if (!itemDef || itemDef.type !== "equipment") continue

    // Check main effects
    for (const effect of itemDef.effects) {
      if (effect.type === "damage-reflect") {
        reflect += effect.value
      }
    }
    // Check bonus effects (for legendary items like Dwarven Pride)
    if (itemDef.bonusEffects) {
      for (const effect of itemDef.bonusEffects) {
        if (effect.type === "damage-reflect") {
          reflect += effect.value
        }
      }
    }
  }

  return reflect
}

// Check if inventory is full
export const isInventoryFull = (inventory: HeroInventory): boolean => {
  return inventory.slots.every((slot) => slot !== null)
}

// Get inventory item count
export const getInventoryItemCount = (inventory: HeroInventory): number => {
  return inventory.slots.filter((slot) => slot !== null).length
}
